import { ref, readonly } from 'vue';
import { uploadOnboardingResume } from '../api/onboarding';
import { useFeatureUsageStore } from '../store/feature-usage.store';
import { bytesToMegaBytes } from '../utils/helpers';

const MAX_FILE_SIZE_MB = 10;

const ERROR_MESSAGES = {
	tooLarge: `Only files up to ${MAX_FILE_SIZE_MB}MB are allowed. Please upload a valid resume.`,
	wrongFormat: 'Your resume has a wrong format. Please upload a PDF, DOCX, or TXT file.',
	rateLimit: 'You have reached your upload limit. Please try again later.',
	network: 'Connection problem. Check your internet connection and try again.',
	generic: 'There was an error uploading your resume, please try with another file.',
};

const mapServerError = (err) => {
	// The API client reports a request that never reached the server as status 0. Asking for
	// another file there would send the user after the wrong problem (EN-1128).
	if (err?.status === 0) {
		return ERROR_MESSAGES.network;
	}
	if (err?.status === 429) {
		return ERROR_MESSAGES.rateLimit;
	}
	if (err?.status === 422 || err?.status === 400) {
		const fieldMessage = err.fieldErrors?.file?.[0];
		if (fieldMessage) {
			return /mimes|file must be/i.test(fieldMessage) ? ERROR_MESSAGES.wrongFormat : fieldMessage;
		}
		return err.message || ERROR_MESSAGES.generic;
	}
	return ERROR_MESSAGES.generic;
};

export const useOnboardingResumeUpload = ({ upload = uploadOnboardingResume } = {}) => {
	const isUploading = ref(false);
	const progress = ref(0);
	const error = ref(null);
	const result = ref(null);
	const fileName = ref('');

	const reset = () => {
		isUploading.value = false;
		progress.value = 0;
		error.value = null;
		result.value = null;
		fileName.value = '';
	};

	const uploadFile = async (file) => {
		if (!file) {
			return;
		}

		reset();
		fileName.value = file.name;

		if (bytesToMegaBytes(file.size) > MAX_FILE_SIZE_MB) {
			error.value = ERROR_MESSAGES.tooLarge;
			return;
		}

		isUploading.value = true;

		try {
			const body = await upload(file, {
				onUploadProgress: (event) => {
					if (event.total) {
						progress.value = Math.round((event.loaded / event.total) * 100);
					}
				},
			});

			result.value = body?.data ?? body ?? true;
		} catch (err) {
			error.value = mapServerError(err);
			if (err?.status === 429) {
				useFeatureUsageStore().setShowLimitExceed('resume_upload');
			}
		} finally {
			isUploading.value = false;
		}
	};

	return {
		isUploading: readonly(isUploading),
		progress: readonly(progress),
		error: readonly(error),
		result: readonly(result),
		fileName: readonly(fileName),
		uploadFile,
		reset,
	};
};
